// src/components/sections/GallerySection.js
import { useEffect, useRef, useState } from 'react';
import '../../../styles/sections.css';

const GallerySection = ({ content }) => {
  const { images = [] } = content;
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleItems, setVisibleItems] = useState(content?.itemsPerLoad || 6);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const scrollRef = useRef(null);
  const autoplayRef = useRef(null);
  const intersectionRef = useRef(null);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Filter active images
  const activeImages = images.filter(image => image.isActive !== false);

  // Auto-scroll for carousel
  useEffect(() => {
    if (content?.displayType === 'carousel' && content?.autoplay && activeImages.length > 1) {
      autoplayRef.current = setInterval(() => {
        if (scrollRef.current) {
          const scrollWidth = scrollRef.current.scrollWidth;
          const clientWidth = scrollRef.current.clientWidth;
          const currentScroll = scrollRef.current.scrollLeft;

          if (currentScroll + clientWidth >= scrollWidth - 10) {
            scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
          }
        }
      }, content?.autoplaySpeed || 3000);

      return () => {
        if (autoplayRef.current) {
          clearInterval(autoplayRef.current);
        }
      };
    }
  }, [content?.displayType, content?.autoplay, content?.autoplaySpeed, activeImages.length]);

  // Progressive loading effect
  useEffect(() => {
    if (content?.progressiveLoading && activeImages.length > visibleItems) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoading) {
            loadMoreItems();
          }
        },
        { threshold: 0.1 }
      );

      if (intersectionRef.current) {
        observer.observe(intersectionRef.current);
      }

      return () => observer.disconnect();
    }
  }, [visibleItems, isLoading, activeImages.length, content?.progressiveLoading]);

  // Animation and parallax effects
  useEffect(() => {
    if (content?.loadAnimation && content?.loadAnimation !== 'none') {
      const items = document.querySelectorAll('.gallery-item');
      items.forEach((item, index) => {
        item.style.setProperty('--animation-duration', `${content?.animationDuration || 600}ms`);
        item.style.setProperty('--transition-timing', content?.transitionTiming || 'ease');
        item.style.setProperty('--stagger-delay', `${content?.staggerDelay || 100}ms`);

        item.classList.add(`animate-${content.loadAnimation}`);
      });
    }

    if (content?.parallaxEffect) {
      const handleScroll = () => {
        const items = document.querySelectorAll('.gallery-item.parallax-enabled');
        items.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const speed = content?.parallaxSpeed || 0.5;
          const yPos = -(rect.top * speed);
          item.style.setProperty('--parallax-offset', `${yPos}px`);
        });
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [content?.loadAnimation, content?.animationDuration, content?.transitionTiming, content?.staggerDelay, content?.parallaxEffect, content?.parallaxSpeed]);

  // Advanced protection hook
  useEffect(() => {
    const protectionStyles = document.createElement('style');
    protectionStyles.textContent = `
      .gallery-section img, 
      .gallery-section video, 
      .gallery-section canvas {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-user-drag: none !important;
        -webkit-touch-callout: none !important;
        pointer-events: none !important;
      }
      
      .gallery-section .gallery-item {
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
      }
    `;
    document.head.appendChild(protectionStyles);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === 'IMG' || node.nodeName === 'VIDEO') {
            node.style.pointerEvents = 'none';
            node.draggable = false;
            node.oncontextmenu = () => false;
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    const detectDevTools = () => {
      const threshold = 160;
      if (
        window.outerHeight - window.innerHeight > threshold ||
        window.outerWidth - window.innerWidth > threshold
      ) {
        document.querySelectorAll('.gallery-section img, .gallery-section video').forEach(el => {
          el.style.filter = 'blur(10px)';
          el.style.opacity = '0.1';
        });
      } else {
        document.querySelectorAll('.gallery-section img, .gallery-section video').forEach(el => {
          el.style.filter = 'none';
          el.style.opacity = '1';
        });
      }
    };

    const devToolsInterval = setInterval(detectDevTools, 100);

    return () => {
      if (protectionStyles.parentNode) {
        document.head.removeChild(protectionStyles);
      }
      observer.disconnect();
      clearInterval(devToolsInterval);
    };
  }, []);

  // Event handlers for protection
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };
  const renderLoadingAnimation = () => {
    const {
      loadingAnimationType = 'default',
      customLoadingImage,
      loadingImageSize = '48px',
      loadingBackgroundColor = '#ffffff',
      loadingBackgroundOpacity = 0.9,
      loadingText = 'Loading...',
      showLoadingText = true,
      skeletonColor = '#f0f0f0',
      skeletonHighlightColor = '#e0e0e0',
      loadingAnimationSpeed = 'normal'
    } = content;

    const speedClass = loadingAnimationSpeed === 'slow' ? 'slow' : loadingAnimationSpeed === 'fast' ? 'fast' : '';

    // Create background color with opacity
    const rgbaBackground = loadingBackgroundColor === '#ffffff'
      ? `rgba(255, 255, 255, ${loadingBackgroundOpacity})`
      : loadingBackgroundColor.startsWith('#')
        ? (() => {
          const hex = loadingBackgroundColor.replace('#', '');
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          return `rgba(${r}, ${g}, ${b}, ${loadingBackgroundOpacity})`;
        })()
        : loadingBackgroundColor;

    return (
      <div
        className={`gallery-loading-custom ${speedClass}`}
        style={{
          backgroundColor: rgbaBackground
        }}
      >
        {/* rest of the component remains the same */}
        {loadingAnimationType === 'custom' && customLoadingImage ? (
          <img
            src={customLoadingImage}
            alt="Loading..."
            style={{
              width: loadingImageSize,
              height: loadingImageSize,
              maxWidth: loadingImageSize,
              maxHeight: loadingImageSize,
              objectFit: 'contain'
            }}
          />
        ) : loadingAnimationType === 'skeleton' ? (
          <div
            className="gallery-skeleton"
            style={{
              '--skeleton-color': skeletonColor,
              '--skeleton-highlight-color': skeletonHighlightColor,
              width: '100%',
              height: '200px'
            }}
          />
        ) : loadingAnimationType === 'pulse' ? (
          <div className="gallery-pulse-loading" style={{ width: loadingImageSize, height: loadingImageSize, backgroundColor: skeletonColor, borderRadius: '50%' }} />
        ) : (
          <div className="gallery-spinner" />
        )}

        {showLoadingText && (
          <p style={{ marginTop: '1rem', color: '#666' }}>
            {loadingText}
          </p>
        )}
      </div>
    );
  };
  const handleDragStart = (e) => {
    e.preventDefault();
    return false;
  };

  const handleSelectStart = (e) => {
    e.preventDefault();
    return false;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'F12' ||
      (e.ctrlKey && e.shiftKey && e.key === 'I') ||
      (e.ctrlKey && e.key === 'u') ||
      (e.ctrlKey && e.key === 's')) {
      e.preventDefault();
      return false;
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('selectstart', handleSelectStart);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('selectstart', handleSelectStart);
    };
  }, []);

  // Load more items function
  const loadMoreItems = () => {
    if (isLoading) return;

    setIsLoading(true);
    setTimeout(() => {
      setVisibleItems(prev => Math.min(prev + (content?.itemsPerLoad || 6), activeImages.length));
      setIsLoading(false);
    }, 500);
  };

  // Early return after hooks
  if (!activeImages.length) return null;

  const openModal = (image) => {
    if (image.clickable === false) return;

    switch (image.onClickAction) {
      case 'modal':
        setSelectedImage(image);
        break;
      case 'link':
        if (image.linkUrl) {
          window.open(image.linkUrl, '_blank');
        }
        break;
      case 'download':
        if (image.url) {
          const link = document.createElement('a');
          link.href = image.url;
          link.download = image.alt || 'download';
          link.click();
        }
        break;
      default:
        break;
    }
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const getContainerStyles = () => {
    const {
      displayType = 'grid',
      gridColumns = { mobile: 1, tablet: 2, desktop: 3, widescreen: 4 },
      flexItemsPerRow = { mobile: 1, desktop: 3 },
      carouselVisibleItems = { mobile: 1, desktop: 3 },
      masonryColumns = { mobile: 1, desktop: 3 },
      gapSize = '1.5rem',
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      enableScrolling,
      containerPadding,
      maxWidth,
      minHeight,
      boxShadow,
      flexDirection = 'row',
      flexJustifyContent = 'flex-start',
      flexAlignItems = 'stretch',
      flexItemGrow = '0',
      flexItemShrink = '1',
      flexItemBasis = 'auto',
      carouselScrollBehavior = 'smooth',
      carouselItemWidth = '256px',
      carouselCenterMode = false,
      customScrollbar = false,
      scrollbarHeight = '8px',
      scrollbarTrackColor = '#f1f1f1',
      scrollbarThumbColor = '#c1c1c1',
      scrollbarThumbRadius = '10px',
      masonryColumnFill = 'balance',
      masonryBreakInside = 'avoid'
    } = content;

    const styles = {};
    let className = 'gallery-container';
    if (content?.galleryWidth) styles.width = content.galleryWidth;
    if (content?.galleryHeight) styles.height = content.galleryHeight;
    if (displayType === 'carousel') {
      className += ' gallery-carousel gallery-carousel-enhanced';
      if (enableScrolling !== false) {
        styles.overflowX = 'auto';
        styles.scrollBehavior = carouselScrollBehavior;

        if (customScrollbar) {
          className += ' custom-scrollbar';
          styles['--scrollbar-height'] = scrollbarHeight;
          styles['--scrollbar-track-color'] = scrollbarTrackColor;
          styles['--scrollbar-thumb-color'] = scrollbarThumbColor;
          styles['--scrollbar-thumb-radius'] = scrollbarThumbRadius;
        } else {
          className += ' scrollbar-hide';
        }
      }

      if (carouselCenterMode) {
        className += ' gallery-carousel-center';
      }

      styles['--carousel-item-width'] = carouselItemWidth;

    } else if (displayType === 'flex') {
      className += ' gallery-flex gallery-flex-responsive';
      styles.display = 'flex';
      styles.flexDirection = flexDirection;
      styles.flexWrap = 'wrap';
      styles.justifyContent = flexJustifyContent;
      styles.alignItems = flexAlignItems;

      if (flexItemsPerRow.desktop === 'auto') {
        className += ` desktop-auto`;
      } else {
        className += ` items-per-row-${flexItemsPerRow.desktop || 3}`;
      }

      if (flexItemsPerRow.mobile) {
        className += ` mobile-${flexItemsPerRow.mobile}`;
      }

      styles['--flex-item-grow'] = flexItemGrow;
      styles['--flex-item-shrink'] = flexItemShrink;
      styles['--flex-item-basis'] = flexItemBasis;
      styles['--flex-item-min-width'] = '200px';

    } else if (displayType === 'masonry') {
      className += ' gallery-masonry gallery-masonry-responsive';
      styles['--masonry-columns'] = masonryColumns.desktop || 3;
      styles['--masonry-columns-mobile'] = masonryColumns.mobile || 1;
      styles['--masonry-columns-tablet'] = masonryColumns.tablet || 2;
      styles['--masonry-fill'] = masonryColumnFill;
      styles['--masonry-break-inside'] = masonryBreakInside;
      styles['--masonry-gap'] = gapSize;
    } else {
      // Enhanced grid layout
      className += ' gallery-grid';
      styles.display = 'grid';

      if (typeof gridColumns === 'object' && gridColumns !== null) {
        const mobileCol = gridColumns.mobile || 1;
        const tabletCol = gridColumns.tablet || 2;
        const desktopCol = gridColumns.desktop || 3;
        const widescreenCol = gridColumns.widescreen || 4;

        styles['--mobile-cols'] = mobileCol;
        styles['--tablet-cols'] = tabletCol;
        styles['--desktop-cols'] = desktopCol;
        styles['--widescreen-cols'] = widescreenCol;

        // Set initial mobile columns
        styles.gridTemplateColumns = `repeat(${mobileCol}, 1fr)`;
        className += ` gallery-responsive-grid`;
      } else {
        // Fallback if gridColumns is not properly set
        styles.gridTemplateColumns = 'repeat(3, 1fr)';
      }
    }

    // Apply common styles
    if (gapSize) styles.gap = gapSize;
    if (backgroundColor && backgroundColor !== '#ffffff') styles.backgroundColor = backgroundColor;
    if (borderWidth && borderWidth !== '0') {
      styles.borderWidth = `${borderWidth}px`;
      styles.borderStyle = 'solid';
      styles.borderColor = borderColor || '#e5e7eb';
    }
    if (borderRadius) {
      const borderRadiusMap = {
        'none': '0px', 'rounded-sm': '4px', 'rounded': '8px',
        'rounded-lg': '12px', 'rounded-xl': '16px', 'rounded-full': '50%'
      };
      styles.borderRadius = borderRadiusMap[borderRadius] || borderRadius;
    }
    if (containerPadding) styles.padding = containerPadding;
    if (maxWidth) {
      styles.maxWidth = maxWidth;
      styles.marginLeft = 'auto';
      styles.marginRight = 'auto';
    }
    if (minHeight) styles.minHeight = minHeight;
    if (boxShadow && boxShadow !== 'none') {
      const shadowMap = {
        'small': '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        'medium': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        'large': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        'xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      };
      styles.boxShadow = shadowMap[boxShadow] || boxShadow;
    }

    return { styles, className };
  };

  const scrollCarousel = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = content?.carouselScrollAmount || 300;
      const newScrollLeft = direction === 'left'
        ? scrollRef.current.scrollLeft - scrollAmount
        : scrollRef.current.scrollLeft + scrollAmount;

      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: content?.carouselScrollBehavior || 'smooth'
      });
    }
  };

  const getItemStyles = (image, index) => {
    const {
      borderRadius = 'rounded',
      imageHeight = '64',
      captionPosition = 'bottom',
      itemSpacing,
      itemPadding,
      itemBackgroundColor,
      itemBorderWidth,
      itemBorderColor,
      itemShadow
    } = content;

    const styles = {};
    let className = 'gallery-item';

    if (image.hoverEffect && image.hoverEffect !== 'none') {
      className += ` hover-${image.hoverEffect}`;
    }

    if (image.clickable !== false && image.onClickAction !== 'none') {
      className += ' cursor-pointer';
    }

    const borderRadiusMap = {
      '': '0px', 'none': '0px', 'rounded-sm': '4px', 'rounded': '8px',
      'rounded-lg': '12px', 'rounded-xl': '16px', 'rounded-full': '50%'
    };
    styles.borderRadius = borderRadiusMap[borderRadius] || '8px';

    const finalImageHeight = image.height || imageHeight;
    if (finalImageHeight === 'auto') {
      styles.height = 'auto';
    } else if (finalImageHeight && (finalImageHeight.includes('px') || finalImageHeight.includes('rem') || finalImageHeight.includes('%'))) {
      styles.height = finalImageHeight;
    } else if (finalImageHeight) {
      const heightMap = {
        '32': '128px', '48': '192px', '56': '224px', '64': '256px',
        '72': '288px', '80': '320px', '96': '384px'
      };
      styles.height = heightMap[finalImageHeight] || '256px';
    }

    if (image.width) styles.width = image.width;
    if (itemSpacing && content.displayType === 'flex') styles.margin = itemSpacing;
    if (itemPadding) styles.padding = itemPadding;
    if (itemBackgroundColor) styles.backgroundColor = itemBackgroundColor;
    if (itemBorderWidth && itemBorderWidth !== '0') {
      styles.borderWidth = `${itemBorderWidth}px`;
      styles.borderStyle = 'solid';
      styles.borderColor = itemBorderColor || '#e5e7eb';
    }
    if (itemShadow && itemShadow !== 'none') {
      const shadowMap = {
        'small': '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        'medium': '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        'large': '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
      };
      styles.boxShadow = shadowMap[itemShadow] || itemShadow;
    }

    if (captionPosition === 'overlay') {
      styles.overflow = 'hidden';
      styles.position = 'relative';
    }

    return { styles, className };
  };

  const getCaptionStyles = (captionPosition, captionStyle) => {
    const {
      captionFontSize = '14px',
      captionColor = '#666666',
      captionBackgroundColor,
      captionPadding = '8px'
    } = content;

    const styles = {
      fontSize: captionFontSize,
      color: captionColor,
      margin: '0',
      padding: '0'
    };

    let className = 'gallery-caption';

    if (captionPosition === 'top') {
      styles.marginBottom = '8px';
    } else if (captionPosition === 'bottom') {
      styles.marginTop = '8px';
    } else if (captionPosition === 'overlay') {
      styles.position = 'absolute';
      styles.bottom = '0';
      styles.left = '0';
      styles.right = '0';
      styles.background = captionBackgroundColor || 'rgba(0, 0, 0, 0.75)';
      styles.color = captionColor || 'white';
      styles.padding = captionPadding;
      styles.margin = '0';
    }

    if (captionStyle === 'bold') {
      styles.fontWeight = 'bold';
    } else if (captionStyle === 'italic') {
      styles.fontStyle = 'italic';
    } else if (captionStyle === 'underline') {
      styles.textDecoration = 'underline';
    }

    return { styles, className };
  };

  const renderMediaItem = (image, index) => {
    if (content?.progressiveLoading && index >= visibleItems) {
      return null;
    }

    const {
      captionPosition = 'bottom',
      captionStyle = 'normal',
      imageObjectFit = 'cover',
      lazyLoading = true,
      loadAnimation = 'none',
      parallaxEffect = false,
      parallaxDirection = 'up',
      imageQuality = 'high',
      preloadStrategy = 'metadata'
    } = content;

    const { styles: itemStyles, className: itemClassName } = getItemStyles(image, index);
    const { styles: captionStyles } = getCaptionStyles(captionPosition, captionStyle);

    let enhancedClassName = itemClassName;

    if (loadAnimation !== 'none') {
      enhancedClassName += ` animate-${loadAnimation}`;
    }

    if (parallaxEffect) {
      enhancedClassName += ` parallax-enabled gallery-parallax-${parallaxDirection}`;
    }

    if (image.hoverEffect && image.hoverEffect !== 'none') {
      enhancedClassName += ` hover-${image.hoverEffect}`;
    }

    const mediaStyles = {
      width: image.width || '100%',
      height: image.height || '100%',
      objectFit: image.objectFit || imageObjectFit,
      borderRadius: itemStyles.borderRadius,
      transition: 'all 0.3s ease',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      WebkitUserDrag: 'none',
      WebkitTouchCallout: 'none',
      pointerEvents: 'none'
    };

    const protectionProps = {
      onContextMenu: handleContextMenu,
      onDragStart: handleDragStart,
      onSelectStart: handleSelectStart,
      draggable: false,
      unselectable: "on",
      style: mediaStyles,
      className: `quality-${imageQuality}`
    };

    const mediaElement = image.type === 'video' ? (
      <div style={{ position: 'relative' }}>
        <video
          src={image.url}
          alt={image.alt || `Gallery video ${index + 1}`}
          {...protectionProps}
          controls
          muted
          loop
          preload={preloadStrategy}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          crossOrigin="anonymous"
        />
        {!image.controls && (
          <div className="video-play-button">▶</div>
        )}
      </div>
    ) : (
      <img
        src={image.url}
        alt={image.alt || `Gallery image ${index + 1}`}
        {...protectionProps}
        loading={lazyLoading ? "lazy" : "eager"}
        crossOrigin="anonymous"
      />
    );

    return (
      <div
        key={index}
        className={enhancedClassName}
        style={{
          ...itemStyles,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          flexGrow: content?.displayType === 'flex' ? (content?.flexItemGrow || '0') : undefined,
          flexShrink: content?.displayType === 'flex' ? (content?.flexItemShrink || '1') : undefined,
          flexBasis: content?.displayType === 'flex' ? (content?.flexItemBasis || 'auto') : undefined,
          minWidth: content?.displayType === 'carousel' ? (content?.carouselItemWidth || '256px') : undefined,
          maxWidth: content?.displayType === 'carousel' ? (content?.carouselItemWidth || '256px') : undefined
        }}
        onClick={() => openModal(image)}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        onSelectStart={handleSelectStart}
        tabIndex={image.clickable !== false ? 0 : -1}
        role={image.clickable !== false ? "button" : "img"}
        aria-label={image.alt || `Gallery item ${index + 1}`}
      >
        {captionPosition === 'top' && image.caption && (
          <p style={captionStyles}>{image.caption}</p>
        )}

        <div style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}>
          {mediaElement}

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            pointerEvents: 'auto',
            backgroundColor: 'transparent'
          }}
            onContextMenu={handleContextMenu}
            onDragStart={handleDragStart}
            onSelectStart={handleSelectStart}
            onClick={() => openModal(image)}
          />

          {image.showOverlay && (
            <div className="image-overlay">
              {image.overlayText || 'View Image'}
            </div>
          )}

          {captionPosition === 'overlay' && image.caption && (
            <p style={{ ...captionStyles, zIndex: 2 }}>{image.caption}</p>
          )}
        </div>

        {captionPosition === 'bottom' && image.caption && (
          <p style={captionStyles}>{image.caption}</p>
        )}
      </div>
    );
  };

  const { styles: containerStyles, className: containerClassName } = getContainerStyles();

  return (
    <div className="gallery-section" style={{ padding: content?.sectionPadding || '2rem 0' }}>
      {/* Section Title */}
      {content?.title && (
        <div style={{
          textAlign: content?.titleAlignment || 'center',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: content?.titleFontSize || '2rem',
            color: content?.titleColor || '#333',
            fontWeight: content?.titleFontWeight || 'bold',
            margin: 0
          }}>
            {content.title}
          </h2>
          {content?.subtitle && (
            <p style={{
              fontSize: content?.subtitleFontSize || '1.125rem',
              color: content?.subtitleColor || '#666',
              marginTop: '0.5rem',
              margin: 0
            }}>
              {content.subtitle}
            </p>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        className={containerClassName}
        style={containerStyles}
      >
        {activeImages.map((image, index) => renderMediaItem(image, index))}
      </div>

      {/* Carousel Navigation */}
      {content?.displayType === 'carousel' && content?.showNavigation !== false && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1rem',
          gap: '0.5rem'
        }}>
          <button
            onClick={() => scrollCarousel('left')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: content?.navigationColor || '#e5e7eb',
              color: content?.navigationTextColor || '#374151',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = content?.navigationHoverColor || '#d1d5db';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = content?.navigationColor || '#e5e7eb';
            }}
            aria-label="Previous items"
          >
            ←
          </button>
          <button
            onClick={() => scrollCarousel('right')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: content?.navigationColor || '#e5e7eb',
              color: content?.navigationTextColor || '#374151',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = content?.navigationHoverColor || '#d1d5db';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = content?.navigationColor || '#e5e7eb';
            }}
            aria-label="Next items"
          >
            →
          </button>
        </div>
      )}

      {/* Carousel Dots */}
      {content?.displayType === 'carousel' && content?.carouselShowDots && (
        <div className="carousel-dots">
          {Array.from({
            length: Math.ceil(activeImages.length / (content?.carouselVisibleItems?.desktop || 3))
          }).map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${currentPage === index ? 'active' : ''}`}
              onClick={() => {
                setCurrentPage(index);
                if (scrollRef.current) {
                  const itemWidth = parseInt(content?.carouselItemWidth) || 256;
                  const visibleItems = content?.carouselVisibleItems?.desktop || 3;
                  const scrollPosition = index * itemWidth * visibleItems;
                  scrollRef.current.scrollTo({
                    left: scrollPosition,
                    behavior: content?.carouselScrollBehavior || 'smooth'
                  });
                }
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Carousel Counter */}
      {content?.displayType === 'carousel' && content?.carouselShowCounter && (
        <div className="carousel-counter">
          {currentPage + 1} / {Math.ceil(activeImages.length / (content?.carouselVisibleItems?.desktop || 3))}
        </div>
      )}

      {/* Progressive Loading */}
      {content?.progressiveLoading && visibleItems < activeImages.length && (
        <div className="gallery-load-more">
          {isLoading ? renderLoadingAnimation() : (
            <button onClick={loadMoreItems}>
              Load More ({activeImages.length - visibleItems} remaining)
            </button>
          )}
          <div ref={intersectionRef} style={{ height: '1px' }}></div>
        </div>
      )}
      {/* Modal with Enhanced Protection */}
      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: content?.modalBackgroundColor || 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: content?.modalBlur ? 'blur(4px)' : 'none'
          }}
          onClick={closeModal}
          onContextMenu={handleContextMenu}
        >
          <div
            style={{
              maxWidth: content?.modalMaxWidth || '90vw',
              maxHeight: content?.modalMaxHeight || '90vh',
              padding: '1rem',
              borderRadius: content?.modalBorderRadius || '8px',
              backgroundColor: content?.modalContentBackground || 'transparent'
            }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={handleContextMenu}
          >
            {selectedImage.type === 'video' ? (
              <video
                src={selectedImage.url}
                alt={selectedImage.alt || 'Gallery video'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: content?.modalBorderRadius || '8px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  pointerEvents: 'none'
                }}
                controls
                autoPlay
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                onContextMenu={handleContextMenu}
                onDragStart={handleDragStart}
                crossOrigin="anonymous"
              />
            ) : (
              <img
                src={selectedImage.url}
                alt={selectedImage.alt || 'Gallery image'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: content?.modalBorderRadius || '8px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitUserDrag: 'none',
                  pointerEvents: 'none'
                }}
                onContextMenu={handleContextMenu}
                onDragStart={handleDragStart}
                onSelectStart={handleSelectStart}
                draggable={false}
                crossOrigin="anonymous"
              />
            )}
            {selectedImage.caption && (
              <p style={{
                color: content?.modalCaptionColor || 'white',
                textAlign: 'center',
                marginTop: '1rem',
                fontSize: content?.modalCaptionFontSize || '1rem',
                backgroundColor: content?.modalCaptionBackground || 'transparent',
                padding: content?.modalCaptionPadding || '0',
                borderRadius: content?.modalCaptionBorderRadius || '0'
              }}>
                {selectedImage.caption}
              </p>
            )}
          </div>
          <button
            onClick={closeModal}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              color: content?.modalCloseColor || 'white',
              fontSize: '1.5rem',
              background: content?.modalCloseBackground || 'rgba(0, 0, 0, 0.5)',
              border: 'none',
              borderRadius: '50%',
              width: '2.5rem',
              height: '2.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = content?.modalCloseHoverBackground || 'rgba(0, 0, 0, 0.7)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = content?.modalCloseBackground || 'rgba(0, 0, 0, 0.5)';
            }}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default GallerySection;





// / /working simple before saba edition
// // src/components/sections/GallerySection.js
// import React, { useState } from 'react';
// import '../../../styles/sections.css';

// const GallerySection = ({ content }) => {
//   const { images = [] } = content;
//   const [selectedImage, setSelectedImage] = useState(null);

//   if (!images.length) return null;

//   const openModal = (image) => {
//     setSelectedImage(image);
//   };

//   const closeModal = () => {
//     setSelectedImage(null);
//   };

//   return (
//     <div className="py-8">
//       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
//         {images.map((image, index) => (
//           <div
//             key={index}
//             className="cursor-pointer hover:opacity-90 transition-opacity"
//             onClick={() => openModal(image)}
//           >
//             <img
//               src={image.url}
//               alt={image.alt || `Gallery image ${index + 1}`}
//               className="w-full h-64 object-cover rounded"
//             />
//             {image.caption && (
//               <p className="text-sm text-gray-600 mt-2">{image.caption}</p>
//             )}
//           </div>
//         ))}
//       </div>

//       {selectedImage && (
//         <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={closeModal}>
//           <div className="max-w-4xl max-h-screen p-4">
//             <img
//               src={selectedImage.url}
//               alt={selectedImage.alt || 'Gallery image'}
//               className="max-w-full max-h-[80vh] object-contain"
//             />
//             {selectedImage.caption && (
//               <p className="text-white text-center mt-4">{selectedImage.caption}</p>
//             )}
//           </div>
//           <button
//             onClick={closeModal}
//             className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300"
//           >
//             &times;
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default GallerySection;
